import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import LegalFooter from '../components/LegalFooter';
import './LoginPage.css';

const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialToken = searchParams.get('token') || '';

  const [email, setEmail] = useState('');
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetLink, setResetLink] = useState(initialToken ? '' : null);

  const isResetStage = Boolean(token);

  const handleRequestReset = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    try {
      setLoading(true);
      const response = await authService.requestPasswordReset(email);
      setSuccess(response.message || 'Password reset link generated.');

      if (response.resetToken) {
        setToken(response.resetToken);
        setResetLink(response.resetUrl || '');
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        requestError.response?.data?.message ||
        'Unable to generate a password reset link right now.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Reset token is missing. Please request a new reset link.');
      return;
    }

    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      const response = await authService.resetPassword(token, password);
      setSuccess(response.message || 'Password reset successful.');
      window.setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (resetError) {
      setError(
        resetError.response?.data?.error ||
        resetError.response?.data?.message ||
        'Unable to reset password right now.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Creator Yatra</h1>
          <p>{isResetStage ? 'Create a new password' : 'Reset your password'}</p>
        </div>

        <form className="login-form" onSubmit={isResetStage ? handleResetPassword : handleRequestReset}>
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message" role="status">
              {success}
            </div>
          )}

          {!isResetStage && (
            <>
              <div className="form-group">
                <label htmlFor="reset-email">Email</label>
                <input
                  type="email"
                  id="reset-email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Enter your email"
                  disabled={loading}
                  autoComplete="email"
                  required
                />
              </div>

              {resetLink && (
                <div className="info-message">
                  <strong>Reset link generated:</strong>
                  <a href={resetLink}>{resetLink}</a>
                </div>
              )}
            </>
          )}

          {isResetStage && (
            <>
              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter new password"
                    disabled={loading}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    disabled={loading}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Confirm new password"
                  disabled={loading}
                  autoComplete="new-password"
                  required
                />
              </div>
            </>
          )}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Please wait...' : isResetStage ? 'Reset Password' : 'Generate Reset Link'}
          </button>
        </form>

        <div className="login-footer">
          <LegalFooter showBackToLogin backToLoginLabel="Back to sign in" />
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
